import { createSlice } from "@reduxjs/toolkit";

function buildRowLookupMaps(pageData) {
    const byCaseId = Object.create(null);
    const byCaseAndAppointment = Object.create(null);
    const rows = pageData?.data;
    if (!Array.isArray(rows)) {
        return { byCaseId, byCaseAndAppointment };
    }
    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const caseId = row?.case?.id;
        if (caseId == null) continue;
        const caseKey = String(caseId);
        byCaseId[caseKey] = i;
        const appointmentId = row?.appointment?.id;
        if (appointmentId != null) {
            byCaseAndAppointment[`${caseKey}:${String(appointmentId)}`] = i;
        }
    }
    return { byCaseId, byCaseAndAppointment };
}

function refreshCurrentPageRowLookup(state) {
    const maps = buildRowLookupMaps(state.currentpagedata);
    state.currentPageRowLookup = maps;
}

function resolveCurrentPageRowIndex(state, payload) {
    const rows = state.currentpagedata?.data;
    if (!Array.isArray(rows) || !rows.length || payload?.case_id == null) {
        return -1;
    }
    const caseKey = String(payload.case_id);
    const appointmentId = payload?.appointment_id;
    const lookup = state.currentPageRowLookup;

    let idx = -1;
    if (appointmentId != null) {
        const composite = `${caseKey}:${String(appointmentId)}`;
        const hit = lookup?.byCaseAndAppointment?.[composite];
        if (Number.isInteger(hit) && hit >= 0 && hit < rows.length) {
            const row = rows[hit];
            if (row?.case?.id == payload.case_id && row?.appointment?.id == appointmentId) {
                return hit;
            }
        }
        idx = rows.findIndex(
            (row) => row?.case?.id == payload.case_id && row?.appointment?.id == appointmentId,
        );
    } else {
        const hit = lookup?.byCaseId?.[caseKey];
        if (Number.isInteger(hit) && hit >= 0 && hit < rows.length) {
            const row = rows[hit];
            if (row?.case?.id == payload.case_id) {
                return hit;
            }
        }
        idx = rows.findIndex((row) => row?.case?.id == payload.case_id);
    }
    if (idx !== -1) {
        refreshCurrentPageRowLookup(state);
    }
    return idx;
}

const initialFilterState = {
    currentpage: 1,
    currentpagedata: null,
    listFiltersParams: null,
    filters: {
        // case_status: null,
        // case_type: null,
        // location: null,
        // practice: null,
        // year: null
    },
    pageResults: null,
    isAPracticeUsedAsFilter: null,
    snapshotData: null,
    snapshotDataVerification: null,
    orderFilters: null,
    loading: false,
    workqSlug: null,
    exceptions: {},
    workqSearch: 0,
    searchParams: {},
    requesterId: null,
    savedViewsBySlug: {},
    savedViewsLoaded: false,
    activeSavedViewSlug: null,
    currentPageRowLookup: {
        byCaseId: Object.create(null),
        byCaseAndAppointment: Object.create(null),
    },
}

const filterSlice = createSlice({
    name: 'filter',
    initialState: initialFilterState,
    reducers: {
        // setActivepatient(state, data) {
        //     state.activePatient = data.payload
        // },   
        setCurrentPage(state, data) {
            state.currentpage = data.payload
        },
        setCurrentPageData(state, data) {
            state.currentpagedata = data.payload
            refreshCurrentPageRowLookup(state)
        },
        saveListFiltersParams(state, data) {
            state.listFiltersParams = data.payload
        },
        setFilters(state, data) {
            state.filters[data.payload.filter] = data.payload.value
        },
        setFiltersMultiple(state, data) {
            state.filters = data.payload
        },
        removeSingleFilter(state, data) {
            if (!data.payload.multiple) { // if its not for a multiple selection, simply remove
                state.filters[data.payload.filter] = null
                // delete state.filters[data.payload.filter]
            } else { // multiple selection!!!         
                const currentFilterValue = state.filters[data.payload.filter]
                if (!Array.isArray(currentFilterValue)) {
                    return
                }

                const itemIndex = currentFilterValue.findIndex(object => {
                    // return object.value == data.payload.value   
                    if (data.payload.isAsync) {
                        return object.value == data.payload.value
                    }
                    return object == data.payload.value                  
                })

                if (itemIndex !== -1) {
                    if (currentFilterValue.length == 1) {
                        state.filters[data.payload.filter] = null
                        // delete state.filters[data.payload.filter]
                    } else {
                        currentFilterValue.splice(itemIndex, 1)
                    }      
                    
                }
            }
            // state.filters[data.payload.filter] = null
        },
        removePayerTypeStatus(state, data) {
            if (data.payload.filter == 'type') {
                if (state.filters.payer_type_filters) {
                    const itemIndex = state.filters.payer_type_filters.findIndex(object => {                        
                        return object.payer_type_id == data.payload.value                  
                    })

                    if (itemIndex !== -1) {
                        if (state.filters.payer_type_filters.length == 1) {
                            state.filters.payer_type_filters = null
                        } else {
                            state.filters.payer_type_filters.splice(itemIndex, 1)
                        } 
                    }
                }
            }

            if (data.payload.filter == 'status') {
                if (state.filters.payer_type_filters) {
                    const itemIndex = state.filters.payer_type_filters.findIndex(object => {                        
                        return object.payer_type_id == data.payload.payer_type_id                  
                    })

                    if (itemIndex !== -1) {
                        if (state.filters.payer_type_filters[itemIndex] && state.filters.payer_type_filters[itemIndex].payer_status_id) {
                            const statusIndex = state.filters.payer_type_filters[itemIndex].payer_status_id.findIndex(object => {                        
                                return object == data.payload.value                  
                            })
                            if (statusIndex !== -1) {
                                if (state.filters.payer_type_filters[itemIndex].payer_status_id.length == 1) {
                                    state.filters.payer_type_filters[itemIndex].payer_status_id = null
                                } else {
                                    state.filters.payer_type_filters[itemIndex].payer_status_id.splice(statusIndex, 1)
                                } 
                            }
                        }
                    }
                }
            }
        },
        clearFilters(state) {
            state.filters = {
                case_status: null,
                case_type: null,
                location: null,
                practice: null,
                year: null
            }
        },
        setExceptionsBatch(state, data) {
            state.exceptions = data.payload
        },
        removeSingleException(state, data) {
            state.exceptions[data.payload] = null
        },
        setPageResults(state, data) {
            state.pageResults = data.payload
        },
        setIsAPracticeUsedAsFilter(state, data) {
            state.isAPracticeUsedAsFilter = data.payload
        },
        setSnapshotData(state, data) {
            state.snapshotData = data.payload
        },
        setSnapshotDataVerification(state, data) {
            state.snapshotDataVerification = data.payload
        },
        setSnapshotDataGeneral(state, data) {
            state.snapshotDataGeneral = data.payload
        },
        setOrderFilters(state, data) {
            state.orderFilters = data.payload
        },
        setMasterLoading(state, data) {
            state.loading = data.payload
        },
        replaceDocumentInPractices(state, data) {
            if (state.currentpagedata && state.currentpagedata.data) {
                let index = state.currentpagedata.data.findIndex(object => {
                    return object.case.id == data.payload.case_id
                })

                if (index != -1) {
                    let practiceIndex = state.currentpagedata.data[index].practices?.findIndex(object => {
                        return object.id == data.payload.case_practice_id
                    })

                    if (practiceIndex != -1) {
                        state.currentpagedata.data[index].practices[practiceIndex].mrq_upload = data.payload.newDoc
                    }
                    
                }
            }

        },
        updateItemInWorkQRow(state, data) {
            let info = data.payload
            if (state.currentpagedata && state.currentpagedata.data) {
                if (!info?.folder || !info?.item) {
                    return
                }
                let index = resolveCurrentPageRowIndex(state, data.payload)

                if (index != -1) {       
                    const row = state.currentpagedata.data[index]
                    const targetFolder = row?.[info.folder]
                    if (!targetFolder) {
                        return
                    }
                    if (Array.isArray(targetFolder)) { // folder is an array
                        if (info.item.itemId == null || !Array.isArray(info.item.fields) || !info.item.fields.length) {
                            return
                        }
                        const folderIndex = targetFolder.findIndex((element) => element?.id == info.item.itemId)
                        if (folderIndex === -1) {
                            return
                        }
                        const element = targetFolder[folderIndex]
                        info.item.fields.forEach((el) => {
                            if (!el || el.field == null) return
                            if (element[el.field] === el.value) return
                            element[el.field] = el.value
                        })
                    } else { //folder is object
                        if (info.item.complexFields) { // complex fields for aica department status on work becasue we can't have the same structure for teh same data and I need to modify each and every one diffenrently
                            info.item.complexFields.forEach((el) => {
                                if (!el || !el.prop || !Array.isArray(el.fields)) return
                                const propNode = targetFolder?.[el.prop]
                                if (!propNode || typeof propNode !== "object") return
                                el.fields.forEach((el2) => {
                                    if (!el2 || el2.field == null) return
                                    if (propNode[el2.field] === el2.value) return
                                    propNode[el2.field] = el2.value
                                })
                            })
                        } else {
                            if (!Array.isArray(info.item.fields) || !info.item.fields.length) {
                                return
                            }
                            info.item.fields.forEach((el) => {
                                if (!el || el.field == null) return
                                if (targetFolder[el.field] === el.value) return
                                targetFolder[el.field] = el.value
                            }) 
                        }
                        
                    }                    
                    
                }
            }
        },
        pushWholeItemInArray(state, data) {
            if (state.currentpagedata && state.currentpagedata.data) {
                let index = state.currentpagedata.data.findIndex(object => {
                    return object.case.id == data.payload.case_id
                })

                if (index != -1) {
                    if (state.currentpagedata.data[index][data.payload.folder]) {
                        const folderindex = state.currentpagedata.data[index][data.payload.folder].findIndex(object => {
                            return object.id == data.payload.itemId                  
                        })
                        if (folderindex !== -1) {                
                            state.currentpagedata.data[index][data.payload.folder][folderindex] = data.payload.item
                        } else {
                            state.currentpagedata.data[index][data.payload.folder].unshift(data.payload.item)
                        }
                    }
                }
            }
            
        },
        removeWholeItemFromArray(state, data) {
            if (state.currentpagedata && state.currentpagedata.data) {
                let index = state.currentpagedata.data.findIndex(object => {
                    return object.case.id == data.payload.case_id
                })

                if (index != -1) {
                    if (state.currentpagedata.data[index][data.payload.folder]) {
                        const folderindex = state.currentpagedata.data[index][data.payload.folder].findIndex(object => {
                            return object.id == data.payload.itemId                  
                        })
                        if (folderindex !== -1) {              
                            // state.currentpagedata.data[index][data.payload.folder][folderindex] = data.payload.item
                            state.currentpagedata.data[index][data.payload.folder].splice(folderindex, 1)
                        } 
                    }
                }
            }
            
        },
        batchUpdate(state, data) {
            if (!data.payload || !state.currentpagedata?.data) {
                return
            }

                let index = state.currentpagedata.data.findIndex(object => {
                    return object.case.id == data.payload.caseId
                })

                if (index != -1) {
                    if (state.currentpagedata.data[index]) {
                        for (const [key, value] of Object.entries(data.payload.data)) {

                            if (value.constructor == Array) { // check the type of the data
                                state.currentpagedata.data[index][key] = value
                            } else {                
                                // state.activeCase[key][]
                                for (const [insideKey, insideValue] of Object.entries(value)) {
                                    if (state.currentpagedata.data[index][key]) {
                                        state.currentpagedata.data[index][key][insideKey] = insideValue
                                    }
                                    
                                }
                            }         
                        }
                    }
                }
            
        },
        setWorkQslug(state, data) {
            state.workqSlug = data.payload
        },
        clearWorkQSearch(state) {
            state.workqSearch = state.workqSearch+1
        },
        setSearchParams(state, data) {
            state.searchParams = data.payload
        },
        setRequesterId(state, data) {
            state.requesterId = data.payload
        },
        setSavedViews(state, data) {
            const payload = data.payload
            state.savedViewsBySlug = (payload && typeof payload === 'object' && !Array.isArray(payload))
                ? payload
                : {}
            state.savedViewsLoaded = true
        },
        upsertSavedView(state, data) {
            const slug = data.payload?.slug
            if (!slug) {
                return
            }
            state.savedViewsBySlug[slug] = data.payload
            state.savedViewsLoaded = true
        },
        removeSavedView(state, data) {
            const slug = data.payload
            if (!slug || !state.savedViewsBySlug) {
                return
            }
            delete state.savedViewsBySlug[slug]
            if (state.activeSavedViewSlug === slug) {
                state.activeSavedViewSlug = null
            }
        },
        setActiveSavedViewSlug(state, data) {
            state.activeSavedViewSlug = data.payload || null
        },
        resetSavedViews(state) {
            state.savedViewsBySlug = {}
            state.savedViewsLoaded = false
            state.activeSavedViewSlug = null
        },
    }
})

export const filterActions = filterSlice.actions

export default filterSlice.reducer